// Code generated by apic; DO NOT EDIT.
import React from "react";
import type { AuthMeta } from "./types";

/**
 * AuthBadge renders the full authentication policy for a single endpoint.
 *
 * Props:
 *   auth      – the AuthMeta from ServiceMetadata for the endpoint.
 *   className – extra CSS class appended to the root element.
 *
 * All rendering is conditional on data presence; empty/zero values produce no
 * output.  The component has no side effects and no web-global dependencies at
 * module load time (RN-safe).
 */
export function AuthBadge(props: { auth: AuthMeta; className?: string }): JSX.Element {
  const { auth, className } = props;
  const root = ["apic-ui-auth-badge", className].filter(Boolean).join(" ");

  return (
    <div className={root}>
      {/* Mode chip — present when single-mode (composite replaces this) */}
      {auth.mode !== "" && (
        <span className="apic-ui-chip apic-ui-chip--mode">{auth.mode}</span>
      )}

      {/* Composite expression */}
      {auth.composite !== null && auth.composite !== "" && (
        <span className="apic-ui-chip apic-ui-chip--composite">
          {auth.composite}
        </span>
      )}

      {/* RBAC role chips */}
      {auth.roles.length > 0 && (
        <span className="apic-ui-group">
          {auth.roles.map((r) => (
            <span key={r} className="apic-ui-chip apic-ui-chip--role">
              {"role:" + r}
            </span>
          ))}
        </span>
      )}

      {/* OAuth2/OIDC scope chips */}
      {auth.scopes.length > 0 && (
        <span className="apic-ui-group">
          {auth.scopes.map((s) => (
            <span key={s} className="apic-ui-chip apic-ui-chip--scope">
              {s}
            </span>
          ))}
        </span>
      )}

      {/* ABAC attribute chips rendered key=value */}
      {Object.keys(auth.attributes).length > 0 && (
        <span className="apic-ui-group">
          {Object.entries(auth.attributes).map(([k, v]) => (
            <span key={k} className="apic-ui-chip apic-ui-chip--attribute">
              {k + "=" + v}
            </span>
          ))}
        </span>
      )}

      {/* mTLS badge */}
      {auth.mtlsRequired && (
        <span className="apic-ui-badge apic-ui-badge--mtls">
          {"mTLS"}
          {auth.mtlsEku && (
            <span className="apic-ui-badge-note">{" (EKU)"}</span>
          )}
          {auth.mtlsIssuers.length > 0 && (
            <span className="apic-ui-badge-issuers">
              {auth.mtlsIssuers.map((iss) => (
                <span key={iss} className="apic-ui-chip apic-ui-chip--issuer">
                  {iss}
                </span>
              ))}
            </span>
          )}
        </span>
      )}

      {/* CSRF state — show when not "n/a" */}
      {auth.csrf !== "" && auth.csrf !== "n/a" && (
        <span className={"apic-ui-chip apic-ui-chip--csrf apic-ui-chip--csrf-" + auth.csrf}>
          {"csrf:" + auth.csrf}
        </span>
      )}

      {/* WebAuthn ceremony */}
      {auth.webauthn !== "" && (
        <span className="apic-ui-chip apic-ui-chip--webauthn">
          {"webauthn:" + auth.webauthn}
        </span>
      )}

      {/* Webhook marker — shows signature header and secret env-var NAME (never the value) */}
      {auth.webhook && (
        <span className="apic-ui-chip apic-ui-chip--webhook">
          {"webhook"}
          {auth.webhookSignatureHeader !== "" && (
            <span className="apic-ui-badge-note">
              {": " + auth.webhookSignatureHeader}
            </span>
          )}
          {auth.webhookSecretEnv !== "" && (
            <span className="apic-ui-badge-note apic-ui-badge-note--env">
              {" (env: " + auth.webhookSecretEnv + ")"}
            </span>
          )}
        </span>
      )}

      {/* Rate limit */}
      {auth.rateLimit > 0 && (
        <span className="apic-ui-chip apic-ui-chip--rate-limit">
          {auth.rateLimit + " rps"}
        </span>
      )}

      {/* Sensitivity marker — suppress default/uninformative "normal" value.
          Only render when the sensitivity is non-empty and non-default so that
          normal-sensitivity endpoints don't clutter every badge. */}
      {auth.sensitivity !== "" && auth.sensitivity !== "normal" && (
        <span className="apic-ui-chip apic-ui-chip--sensitivity">
          {auth.sensitivity}
        </span>
      )}

      {/* Surface label — suppress the default/uninformative values ("", "public",
          "websocket"). Reserve the chip for elevated surfaces (admin, debug,
          internal, etc.) that are meaningful to a developer reading the badge. */}
      {auth.surface !== "" && auth.surface !== "public" && auth.surface !== "websocket" && (
        <span className="apic-ui-chip apic-ui-chip--surface">
          {"surface:" + auth.surface}
        </span>
      )}
    </div>
  );
}
